import { AlarmClock, ArrowRight, Bot, Brain, CalendarDays, Check, FileText, Lock, Megaphone, Mic, Radar, Target, Wallet, Workflow } from "lucide-react";
import { PLAN_PERKS, PLAN_PRICE_HINT } from "@/components/plans/UpgradeDialog";
import { isStoreApp } from "@/lib/platform";
import { Link } from "react-router";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { StoreBadges } from "@/components/stores/StoreBadges";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { t } from "@shared/i18n";

const MODULES = [
  { icon: Bot, title: t("Coach IA"), text: t("Six modes spécialisés, réponses en temps réel, actions proposées puis validées par vous.") },
  { icon: Target, title: t("Mission Mode"), text: t("Décrivez un objectif : le coach le transforme en mission (tâches, échéances, jalons).") },
  { icon: CalendarDays, title: t("Planification"), text: t("Tâches, projets, agenda, « Ma journée en un clic » et détection de surcharge.") },
  { icon: Wallet, title: t("Trésorerie"), text: t("Suivi, prévisionnel 30/60/90 jours en trois scénarios, simulateur de décisions.") },
  { icon: FileText, title: t("Documents"), text: t("Devis, factures, propositions : générés, vérifiés, exportés en PDF.") },
  { icon: Radar, title: t("Opportunity Radar"), text: t("Factures à relancer, projets bloqués, objectifs en retard : détectés et expliqués.") },
  { icon: Workflow, title: t("Automatisations"), text: t("Déclencheurs, conditions, actions — avec validation humaine et journal.") },
  { icon: Mic, title: t("Copilote vocal"), text: t("Parlez-lui mains libres ; enregistrez vos réunions, il les transcrit.") },
  { icon: AlarmClock, title: t("Rappels intelligents"), text: t("« Rappelle-moi de relancer Paul jeudi » : c'est noté, à l'heure dite.") },
  { icon: Megaphone, title: t("Studio marketing"), text: t("Posts, publicités, client idéal, plan 90 jours : prêts en un clic.") },
  { icon: Brain, title: t("Mémoire contrôlable"), text: t("Le coach retient ce que vous choisissez. Vous voyez et effacez tout.") },
  { icon: Lock, title: t("Confidentialité"), text: t("Données isolées, export et suppression en un clic, RGPD dès la conception.") },
];

export function Landing() {
  return (
    <div className="min-h-dvh">
      <header className="pt-safe glass-bar sticky top-0 z-20 border-b border-border/50">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-8">
          <Logo />
          <nav className="flex items-center gap-1 sm:gap-2">
            <LangToggle />
            <ThemeToggle />
            <Link to="/login" className="press hidden rounded-full px-3 py-2 text-sm font-semibold text-muted hover:text-fg sm:block">
              {t("Connexion")}
            </Link>
            <Link to="/register">
              <Button size="sm">{t("Commencer")}</Button>
            </Link>
          </nav>
        </div>
      </header>

      <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 pb-14 pt-10 sm:px-8 sm:pt-20 lg:grid-cols-[1.25fr_1fr] lg:pb-24">
        <div className="animate-fade-up">
          <span className="glass-card inline-flex items-center gap-2 !rounded-full px-3.5 py-1.5 text-[12px] font-semibold text-muted">
            <span className="size-2 rounded-full bg-brand shadow-[0_0_10px_var(--brand)]" /> {t("Copilote IA pour entrepreneurs")}
          </span>
          <h1 className="mt-6 text-[42px] font-bold leading-[1.02] tracking-tight sm:text-[64px]">
            {t("Votre entreprise avance.")} <span className="bg-gradient-to-r from-[#e8930f] via-[#ffb347] to-[#c27d12] bg-clip-text text-transparent">{t("Votre IA garde une longueur d'avance.")}</span>
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-muted">
            {t("Coach, planning, trésorerie, documents, réunions et automatisations dans une seule app. L'IA propose, vous validez.")}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto">
                {t("Créer mon Copilote")} <ArrowRight className="size-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                {t("J'ai déjà un compte")}
              </Button>
            </Link>
          </div>
          {!isStoreApp && (
            <div className="mt-8">
              <p className="text-[13px] font-semibold text-muted">{t("Aussi sur votre téléphone")}</p>
              <StoreBadges className="mt-3" />
            </div>
          )}
        </div>
        <div className="animate-fade-up relative mx-auto w-full max-w-sm [animation-delay:120ms]">
          <div className="absolute -inset-8 rounded-full bg-[radial-gradient(closest-side,rgb(255_179_71/0.45),transparent)] blur-2xl" aria-hidden />
          <div className="glass-card relative space-y-3 p-4">
            <div className="flex items-center gap-3 rounded-2xl bg-elevated/70 p-3">
              <span className="app-icon size-11 [&_svg]:size-5"><Bot /></span>
              <div className="min-w-0">
                <p className="text-sm font-bold">{t("Briefing du jour")}</p>
                <p className="truncate text-[13px] text-muted">{t("3 priorités · 1 facture à relancer")}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-elevated/70 p-3">
                <p className="text-[12px] font-semibold text-muted">{t("Trésorerie à 30 j")}</p>
                <p className="mt-1 text-xl font-bold tabular-nums">+12 400 €</p>
                <div className="mt-2 flex h-8 items-end gap-1">{[40, 55, 35, 70, 60, 85, 75].map((h, i) => <span key={i} className="flex-1 rounded-sm bg-gradient-to-t from-[#f5a12a] to-[#ffc46e]" style={{ height: `${h}%` }} />)}</div>
              </div>
              <div className="rounded-2xl bg-elevated/70 p-3">
                <p className="text-[12px] font-semibold text-muted">{t("Objectif")}</p>
                <p className="mt-1 text-xl font-bold tabular-nums">72 %</p>
                <div className="mt-3 h-2 rounded-full bg-surface"><div className="h-full w-[72%] rounded-full bg-gradient-to-r from-[#ffc46e] to-[#f5a12a]" /></div>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-elevated/70 p-3">
              <span className="app-icon app-icon-slate size-11 [&_svg]:size-5"><Mic /></span>
              <p className="text-[13px] text-muted">{t("« Prépare le compte rendu de ma réunion avec Martin »")}</p>
            </div>
          </div>
          <p className="mt-3 text-center text-[11px] text-subtle">{t("Illustration de l'interface")}</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-8">
        <h2 className="text-[28px] font-bold tracking-tight sm:text-4xl">{t("Tout votre business, une seule app.")}</h2>
        <div className="stagger mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {MODULES.map((m, i) => (
            <div key={m.title} className="glass-card press p-4 sm:p-5">
              <span className={i % 3 === 2 ? "app-icon app-icon-slate size-12 [&_svg]:size-6" : "app-icon size-12 [&_svg]:size-6"}>
                <m.icon />
              </span>
              <h3 className="mt-4 font-bold">{m.title}</h3>
              <p className="mt-1 text-[13px] leading-snug text-muted">{m.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-8">
        <div className="glass-card grid gap-8 p-6 sm:p-10 md:grid-cols-3">
          {[
            [t("Vous gardez la main"), t("Toute action proposée par l'IA attend votre validation.")],
            [t("Aucune donnée inventée"), t("Le coach distingue faits, hypothèses et estimations, et cite ses sources.")],
            [t("Vos données vous appartiennent"), t("Export complet, suppression, mémoire visible et effaçable.")],
          ].map(([t, d]) => (
            <div key={t}>
              <span className="block h-1 w-10 rounded-full bg-gradient-to-r from-[#ffc46e] to-[#f5a12a]" />
              <h3 className="mt-4 text-lg font-bold">{t}</h3>
              <p className="mt-1.5 text-muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {!isStoreApp && (
        <section id="tarifs" className="mx-auto max-w-6xl px-4 pb-20 sm:px-8">
          <h2 className="text-center text-[28px] font-bold tracking-tight sm:text-[34px]">{t("Un prix pour chaque budget")}</h2>
          <p className="mx-auto mt-2 max-w-xl text-center text-muted">{t("Commencez gratuitement. Passez à la formule supérieure quand votre activité grandit. Sans engagement.")}</p>
          <div className="stagger mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {([
              ["free", t("Découverte"), [t("Coach IA inclus chaque jour"), t("Tâches, agenda, finances"), t("Devis et factures"), t("Rappels et conseil du jour")]],
              ["starter", t("Essentiel"), PLAN_PERKS.starter],
              ["pro", t("Pro"), PLAN_PERKS.pro],
              ["business", t("Business"), PLAN_PERKS.business],
            ] as const).map(([id, name, perks]) => (
              <div key={id} className={id === "pro" ? "glass-card relative p-5 ring-2 ring-brand" : "glass-card relative p-5"}>
                {id === "pro" && <span className="absolute -top-2.5 left-5 rounded-full bg-brand px-2.5 py-0.5 text-[11px] font-bold text-brand-fg">{t("Le plus choisi")}</span>}
                <p className="font-bold">{name}</p>
                <p className="mt-2 text-3xl font-bold">{PLAN_PRICE_HINT[id].split(" /")[0]}<span className="text-sm font-medium text-muted">{id === "free" ? "" : t(" / mois")}</span></p>
                <ul className="mt-4 space-y-1.5 text-[13px]">
                  {perks.map((p) => <li key={p} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-success" />{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/register"><Button size="lg">{t("Commencer gratuitement")} <ArrowRight className="size-4" /></Button></Link>
          </div>
        </section>
      )}

      <StoreBadges className="mx-auto max-w-6xl justify-center px-4 pt-4 sm:px-8" />
      <footer className="pb-safe mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-10 text-sm text-muted sm:flex-row sm:px-8">
        <Logo />
        <div className="flex gap-6">
          <Link to="/help" className="hover:text-fg">
            {t("Aide")}
          </Link>
          <Link to="/legal/confidentialite" className="hover:text-fg">
            {t("Confidentialité")}
          </Link>
          <Link to="/legal/conditions" className="hover:text-fg">
            {t("Conditions")}
          </Link>
        </div>
        <p>© {new Date().getFullYear()} {t("GBC Coach IA · une application NeoTechRams")}</p>
      </footer>
    </div>
  );
}
